import React from 'react'
import classNames from 'classnames'
import { observer } from 'mobx-react'
import { Badge } from '@winnin/winnin_ui.components.badge'
import { Icon } from '@winnin/winnin_ui.components.icon'
import { ProviderIcon } from '@winnin/winnin_ui.components.provider-icon'
import { TooltipWrapper } from '@winnin/winnin_ui.components.tooltip-wrapper'

import AddVideoToCheckBox from 'components/AddVideoToCheckbox'
import LivePostTag from 'components/LivePostTag'
import { PROVIDERS } from 'consts'
import { T, useTranslate } from 'lib/tolgee'
import { humanizeLargeNumber, formatDate, TOLGEE_GLOBALS } from 'utils'

import CreatorCardOnHover from '../CreatorCardOnHover'
import ListMetricVariation from '../ListMetricVariation'
import Thumbnail from '../Thumbnail'
import TopicListSmall from '../TopicListSmall'
import LivePostDate from './components/LivePostDate'
import LivePostMetric from './components/LivePostMetric'
import './index.scss'

const TOLGEE_PREFIX = 'VideoCardList'

const handleViewsValue = (views, hasViewsReaction) => {
  if (hasViewsReaction) {
    return humanizeLargeNumber(views, 1)
  }
  return '--'
}

const handleCommentsValue = (comments, hasCommentsReaction) => {
  if (hasCommentsReaction) {
    return humanizeLargeNumber(comments, 1)
  }
  return '--'
}

const handleLikesValue = (likes, hasLikesReaction, isLikesReactionDisabled) => {
  if (!hasLikesReaction) {
    return '--'
  }
  if (isLikesReactionDisabled) {
    return <Icon icon="eyeClosed" fill="var(--base_color1)" width={20} height={20} />
  }
  return humanizeLargeNumber(likes, 1)
}

const VideoCardList = ({
  title,
  thumbnail,
  index,
  likes,
  hasLikesReaction,
  isLikesReactionDisabled,
  likesVariation,
  comments,
  hasCommentsReaction,
  commentsVariation,
  views,
  hasViewsReaction,
  viewsVariation,
  creator,
  creatorId,
  onClick,
  postId,
  provider,
  publishedAt,
  territories,
  live,
  isPublic = false,
  onCreatorClickCallback,
  shouldEnableCreatorClick = false,
  isPublicity,
  pinId,
  hideCheckbox = false,
}) => {
  const t = useTranslate()
  const isScheduled = live?.state === 'scheduled'
  const providerName = PROVIDERS[provider]?.NAME
  const verifyViews = provider === 'fb'
  const isTwitchProvider = provider === 'tw'

  function handleCardClick() {
    if (isScheduled) {
      return
    }

    onClick?.()
  }

  function getExplanationToLikes() {
    if (isLikesReactionDisabled) {
      return t(TOLGEE_GLOBALS.infoHiddenByCreator)
    }
    return TOLGEE_GLOBALS.platformMetricNotApplicable
  }

  return (
    <div className="VideoCardList" onClick={handleCardClick}>
      <div className="VideoCardList-index">{index < 9 ? `0${index + 1}` : index + 1}</div>

      <div>
        {!isPublic && !hideCheckbox && (
          <AddVideoToCheckBox
            id={postId}
            provider={provider}
            title={title}
            thumbnail={thumbnail}
            creatorName={creator?.title}
            pinId={pinId}
            isModal
          />
        )}
      </div>

      <div
        className={classNames('VideoCardList-image', {
          'tooltipped tooltipped-s tooltipped-multiline': isScheduled,
        })}
        aria-label={t({
          key: `${TOLGEE_PREFIX}-scheduledLiveTooltip`,
          defaultValue: 'The video page will be available once the live starts.',
        })}
      >
        <div className="VideoCardList-image-live-ads-wrapper">
          {isPublicity && (
            <TooltipWrapper tooltipMessage={t(TOLGEE_GLOBALS.paidPartnership)} tooltipStyle="tooltippedS">
              <Badge colorScheme="yellow" className="YellowBadge-customStyle">
                <Icon icon="ads" fill="var(--base_color1)" width={11} height={16} />
              </Badge>
            </TooltipWrapper>
          )}
          <LivePostTag live={live} />
        </div>
        <Thumbnail isFallbackText height={96} src={thumbnail} />
      </div>

      <div className="VideoCardList-title-creator">
        {title && (
          <div className="tooltipped tooltipped-n tooltipped-multiline" aria-label={title}>
            <div className="VideoCardList-title">{title}</div>
          </div>
        )}

        <div className="VideoCardList-creator">
          <div className="VideoCardList-creator-content">
            <span className={`VideoCardList-provider`}>
              {providerName && <ProviderIcon icon={provider} height={20} width={provider === 'yt' ? 25 : 20} />}
            </span>
            <span className="VideoCardList-by">by</span>
            <CreatorCardOnHover
              onCreatorClickCallback={onCreatorClickCallback}
              noHover={isPublic}
              noLink={isPublic && !shouldEnableCreatorClick}
              videoTitle={title}
              type={creator?.__typename}
              title={creator?.title}
              id={creatorId}
              provider={provider}
              groupId={creator?.groupId}
            />
          </div>
        </div>

        <TopicListSmall territories={territories} tooltippedText="Video Topic" />
      </div>

      {live ? (
        <LivePostMetric live={live} views={views} />
      ) : (
        <div
          className={classNames('VideoCardList-views', {
            'tooltipped tooltipped-multiline tooltipped-n': verifyViews,
          })}
          aria-label={t(TOLGEE_GLOBALS.platformViewsNotCollected)}
        >
          {handleViewsValue(views, hasViewsReaction)}
          {!!views && <ListMetricVariation metricVariation={viewsVariation} id={index} />}
          <div className="VideoCardList-metric-label">
            <T keyName={`${TOLGEE_PREFIX}-metricViews`}>Views</T>
          </div>
        </div>
      )}

      <div
        aria-label={t(getExplanationToLikes())}
        className={classNames('VideoCardList-likes', {
          'tooltipped tooltipped-n tooltipped-multiline': isTwitchProvider,
        })}
      >
        {handleLikesValue(likes, hasLikesReaction, isLikesReactionDisabled)}
        {!!likes && <ListMetricVariation metricVariation={likesVariation} id={index} />}
        <div className="VideoCardList-metric-label">
          <T keyName={`${TOLGEE_PREFIX}-metricLikes`}>Likes</T>
        </div>
      </div>

      <div
        aria-label={t(TOLGEE_GLOBALS.platformCommentsNotCollected)}
        className={classNames('VideoCardList-comments', {
          'tooltipped tooltipped-n tooltipped-multiline': isTwitchProvider,
        })}
      >
        {handleCommentsValue(comments, hasCommentsReaction)}
        {!!comments && <ListMetricVariation metricVariation={commentsVariation} id={index} />}
        <div className="VideoCardList-metric-label">
          <T keyName={`${TOLGEE_PREFIX}-metricComments`}>Comments</T>
        </div>
      </div>

      {live ? (
        <LivePostDate live={live} />
      ) : (
        <div className="VideoCardList-date">
          {formatDate(publishedAt)}
          <div className="VideoCardList-metric-label">
            <T keyName={`${TOLGEE_PREFIX}-metricUploadDate`}>Upload date</T>
          </div>
        </div>
      )}
    </div>
  )
}

VideoCardList.propTypes = {}

VideoCardList.defaultProps = {}

VideoCardList.displayName = 'VideoCardList'

export default observer(VideoCardList)
